import { useState } from 'react';
import type { RadioStation } from '@/mocks/radios';
import { getStationContests, contestTypeLabels, contestTypeIcons, contestStatusColors, type Contest } from '@/mocks/adminContests';

interface RadioConcursosSectionProps {
  station: RadioStation;
}

export default function RadioConcursosSection({ station }: RadioConcursosSectionProps) {
  const contests = getStationContests(station.id);
  const [activeTab, setActiveTab] = useState<'activos' | 'finalizados'>('activos');

  const activos = contests.filter((c) => c.status === 'activo' || c.status === 'proximamente');
  const finalizados = contests.filter((c) => c.status === 'finalizado');

  if (contests.length === 0) return null;

  const displayContests = activeTab === 'activos' ? activos : finalizados;

  return (
    <section className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-background-200/70">
        <h3 className="text-base font-bold text-foreground-950 uppercase tracking-wide">Concursos</h3>
        <div className="flex items-center gap-1 bg-background-100 rounded-full p-0.5">
          <button
            onClick={() => setActiveTab('activos')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
              activeTab === 'activos' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Activos ({activos.length})
          </button>
          <button
            onClick={() => setActiveTab('finalizados')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${
              activeTab === 'finalizados' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
            }`}
          >
            Finalizados ({finalizados.length})
          </button>
        </div>
      </div>
      <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {displayContests.slice(0, 6).map((contest) => (
          <ContestCard key={contest.id} contest={contest} />
        ))}
        {displayContests.length === 0 && (
          <div className="col-span-full py-8 text-center text-sm text-foreground-500">
            No hay concursos {activeTab === 'activos' ? 'activos' : 'finalizados'}
          </div>
        )}
      </div>
    </section>
  );
}

function ContestCard({ contest }: { contest: Contest }) {
  const [participating, setParticipating] = useState(false);
  const formatNumber = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };
  const status = contestStatusColors[contest.status] || { dot: 'bg-foreground-400', text: 'text-foreground-500', bg: 'bg-foreground-100' };
  const typeLabel = contestTypeLabels[contest.type] || contest.type;
  const typeIcon = contestTypeIcons[contest.type] || 'ri-gift-line';
  const isActive = contest.status === 'activo';

  return (
    <div className="rounded-xl border border-background-200/70 bg-background-50 hover:border-background-300/60 transition-all p-4">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${status.bg} ${status.text}`}>
            {contest.status === 'activo' ? 'Activo' : contest.status === 'proximamente' ? 'Próximamente' : 'Finalizado'}
          </span>
          <span className="flex items-center gap-1 text-[11px] text-foreground-500">
            <i className={typeIcon}></i>
            {typeLabel}
          </span>
        </div>
      </div>

      <h4 className="text-sm font-bold text-foreground-950">{contest.title}</h4>
      <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{contest.description}</p>

      {/* Prize */}
      <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 rounded-lg px-2 py-1.5 w-fit">
        <i className="ri-trophy-line text-sm"></i>
        {contest.prize}
      </div>

      {/* Dates */}
      <div className="flex items-center gap-4 mt-2 text-[11px] text-foreground-500">
        <span className="flex items-center gap-1"><i className="ri-calendar-line"></i> {contest.startDate}</span>
        <span>→ {contest.endDate}</span>
      </div>

      {/* Winner for finished */}
      {contest.status === 'finalizado' && contest.winnerName && (
        <div className="mt-3 p-2 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-emerald-200 overflow-hidden">
            {contest.winnerAvatar && (
              <img src={contest.winnerAvatar} alt={contest.winnerName} className="w-full h-full object-cover" />
            )}
          </div>
          <div>
            <p className="text-[10px] text-emerald-600 uppercase font-semibold">Ganador</p>
            <p className="text-xs font-bold text-emerald-800">{contest.winnerName}</p>
          </div>
        </div>
      )}

      {/* Stats + Action */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
        <div className="flex items-center gap-3 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1"><i className="ri-user-line"></i> {formatNumber(contest.participants)}</span>
          <span className="flex items-center gap-1"><i className="ri-share-forward-line"></i> {formatNumber(contest.shares)}</span>
        </div>
        {isActive && (
          <button
            onClick={() => setParticipating(!participating)}
            className={`text-[11px] font-semibold cursor-pointer transition-all whitespace-nowrap px-3 py-1 rounded-full ${
              participating
                ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                : 'bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600'
            }`}
          >
            {participating ? 'Participando' : 'Participar'}
          </button>
        )}
      </div>
    </div>
  );
}